import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import { Form, Modal } from "react-bootstrap";
import { v4 as uuidv4 } from "uuid";
import { insertAddUserRequest, insertUpdateUserRequest, insertRemoveUserRequest } from "../Services/ReportingService";
import { NotificationsContext } from "../Contexts/NotificationsContext";
import TimeOfDayModal from "./TimeOfDayModal";
import "../style/UserFormModal.css";
import UserAddOnSection from "./UserPageForms/UserAddOnSection.js";
import UserNameSection from "./UserPageForms/UserNameSection.js";
import DistroListsSection from "./UserPageForms/DistroListsSection.js";
import PhoneNumberSection from "./UserPageForms/PhoneNumberSection.js";
import ExtraInfoSection from "./UserPageForms/ExtraInfoSection.js";
import JobTitleSection from "./UserPageForms/JobTitleSection.js";
import Moment from "moment";
import EmailSearchBlock from "./UserPageForms/EmailSearchBlock.js";
import ModalFooter from "./UserPageForms/ModalFooter.js";
import { DateTime } from "luxon";
import CompanySection from "./UserPageForms/CompanySection.js";
import DateSection from "./UserPageForms/DateSection.js";
import RadioButton from "./RadioButton.js";
import Checkbox from "./Checkbox.js";

// TODO: replace alerts with new notifications

const UserFormModal = (props) => {
  const [submitLoading, setSubmitLoading] = useState(false);
  const [requestType, setRequestType] = useState("");
  const { addNotification } = useContext(NotificationsContext);
  const [showTimeSelectModal, setShowTimeSelectModal] = useState(false);
  const [clickedDateTime, setClickedDateTime] = useState(null);
  const [dateTimeOnCalendarInput, setDateTimeOnCalendarInput] = useState({});
  const [addSecondNumber, setAddSecondNumber] = useState(false);
  const [distroListsAvailable, setDistroListsAvailable] = useState([]);
  const [jobs, setJobs] = useState();
  const [userToAdd, setUserToAdd] = useState({
    fullName: "",
    domainName: "",
    companyName: "",
    licenseBundleId: "",
    showFranchiseAddUser: false,
    showKcAddUser: false,
    accessList: "",
    locationsSupporting: "",
    powerBi: false,
    solver: false,
    avalara: false,
    crmClientsAndContracts: false,
    localMarketingChampions: "",
  });
  const [userToUpdate, setUserToUpdate] = useState({
    effectiveDate: "",
    showTimeSelectModal: false,
    selectedDateTime: null,
    showDistroSelectModal: false,
    isUnplanned: false,
    notifyFranchisee: false,
    submitLoading: false,
    accessList: "",
    phoneNumber1: "",
    phoneNumber1Type: "",
    phoneNumber1Ext: "",
    phoneNumber2: "",
    phoneNumber2Type: "",
    phoneNumber2Ext: "",
    jobTitle: "",
    powerBi: false,
    jetHub: false,
    businessCentralSecurityRole: "",
    fieldSalesAccess: "",
    avalara: false,
    crmClientsAndContracts: false,
    crmContractAndFinancialDetailsAccess: "",
    specialInstructions: "",
    replacement: false,
    personReplacing: "",
    notify: "",
    otherJobTitle: "",
    locationsSupporting: "",
    solver: false,
    localMarketingChampions: "",
  });
  const [userToRemove, setUserToRemove] = useState({
    licenseBundleId: "",
    effectiveDate: "",
    EmailDuration: "",
    emailRecipient: "",
    howDidEmployeeLeave: "",
    whyDidEmployeeLeave: "",
    showTimeSelectModal: false,
    selectedDateTime: null,
    isUnplanned: false,
    notifyFranchisee: false,
    submitLoading: false,
    cityWideYou: false,
    powerBi: false,
    solver: false,
    avalara: false,
    crmClientsAndContracts: false,
  });

  const addUserJobs = useMemo(
    () =>
      [
        { name: "Accountant", requestType: [] },
        { name: "Administrative Assistant", requestType: [] },
        { name: "Business Development Specialist", requestType: [] },
        { name: "CBS Sales Executive", requestType: ["AddKCUser", "AddFranchiseUser"] },
        { name: "Director of Sales", requestType: [] },
        { name: "Facility Solutions Manager", requestType: ["AddKCUser", "AddFranchiseUser"] },
        { name: "Director of National Sales", requestType: ["AddHomeOfficeUser"] },
        { name: "Facility Service Manager", requestType: ["AddHomeOfficeUser"] },
        { name: "Franchise Business Consultant", requestType: ["AddHomeOfficeUser"] },
        { name: "Fran Dev Sales", requestType: ["AddHomeOfficeUser"] },
        { name: "IT", requestType: ["AddHomeOfficeUser"] },
        { name: "Marketing", requestType: ["AddHomeOfficeUser"] },
        { name: "Franchisee", requestType: ["AddKCUser", "AddFranchiseUser"] },
        { name: "General Manager", requestType: ["AddKCUser", "AddFranchiseUser"] },
        { name: "Human Resources", requestType: [] },
        { name: "IC Recruiter", requestType: ["AddKCUser", "AddFranchiseUser"] },
        { name: "Night Manager", requestType: ["AddKCUser", "AddFranchiseUser"] },
        { name: "Operations Manager", requestType: [] },
        { name: "Office Manager", requestType: ["AddKCUser", "AddFranchiseUser"] },
        { name: "Sales Associate", requestType: ["AddKCUser", "AddFranchiseUser"] },
        { name: "Sales Executive", requestType: ["AddKCUser", "AddFranchiseUser"] },
        { name: "Jr. Sales Executive", requestType: ["AddKCUser", "AddFranchiseUser"] },
        { name: "Sales Manager", requestType: ["AddKCUser", "AddFranchiseUser"] },
        { name: "TI Sales", requestType: ["AddKCUser"] },
        { name: "Director of Operations", requestType: ["AddKCUser", "AddFranchiseUser"] },
        { name: "Other (Please Specify)", requestType: [] },
      ].filter((job) => job.requestType.some((type) => requestType == type) || job.requestType.length === 0),
    [requestType]
  );

  const updateUserJobs = useMemo(
    () =>
      [
        "Administrative Assistant",
        "Business Development Specialist",
        "Director of Sales",
        "Sales Manager",
        "Facility Solutions Manager",
        props.typeOfModal === "NotFranchise" ? "TI Sales" : "",
        "General Manager",
        "Sales Executive",
        "Jr. Sales Executive",
        "Sales Associate",
        "Operations Manager",
        "CBS Sales Executive",
        "Director of Operations",
        "Office Manager",
        "Night Manager",
        "IC Recruiter",
        "Human Resources",
        "Accountant",
        "Franchisee",
        "Other (Please Specify)",
      ].filter((job) => job !== ""),
    [props.typeOfModal]
  );

  useEffect(() => {
    if (props.mode === "add") {
      setJobs(addUserJobs);
    } else if (props.mode === "update") {
      setJobs(updateUserJobs);
    }
  }, [props.mode, requestType, addUserJobs, updateUserJobs]);

  useEffect(() => {
    if (props.mode === "add") {
      if (requestType === "AddFranchiseUser") {
        let addFranchiseUserDistroList = ["CWFS JS Sold", "Accounting Group", "IT Communications", "CBS"];

        if (userToAdd.companyName) {
          addFranchiseUserDistroList = addFranchiseUserDistroList.concat([
            `${userToAdd.companyName} - Ops`,
            `${userToAdd.companyName} - Sales`,
            `${userToAdd.companyName} - Acctg`,
            `${userToAdd.companyName} - Employee DL`,
          ]);
        }
        addFranchiseUserDistroList = addFranchiseUserDistroList.concat(["None", "Other (Please Specify) "]);
        setDistroListsAvailable(addFranchiseUserDistroList);
      } else if (requestType == "AddKCUser") {
        if (userToAdd.companyName && userToAdd.companyName !== "Kansas City") {
          const addKcUserNotKcDisroList = [
            "CWFS JS Sold",
            "Accounting Group",
            "IT Communications",
            `${userToAdd.companyName} - Ops`,
            `${userToAdd.companyName} - Sales`,
            `${userToAdd.companyName} - Acctg`,
            `${userToAdd.companyName} - Employee DL`,
            "None",
            "Other (Please Specify) ",
          ];
          setDistroListsAvailable(addKcUserNotKcDisroList);
        }
      } else if (userToAdd.companyName && userToAdd.companyName === "Kansas City") {
        const addKcUserKcDisroList = [
          "CWFS JS Sold",
          "Accounting Group",
          "IT Communications",
          `CWM-${userToAdd.companyName} CBS Sales`,
          "CWM-KS DOOs & FSMs",
          "CWM-KC Night Managers",
          "CBS",
          `${userToAdd.companyName} - Ops`,
          `${userToAdd.companyName} - Sales`,
          "CWFS-KC-AR",
          "CWM-KS Employees",
          `${userToAdd.companyName} - Acctg`,
          "CWM-KC FSMs",
          "CWM-KC Sales",
          "None",
          "Other (Please Specify)",
        ];
        setDistroListsAvailable(addKcUserKcDisroList);
      } else if (requestType === "AddHomeOfficeUser") {
        const addHomeOfficeUserDistroList = [
          "CWFS JS Sold",
          "Accounting Group",
          "IT Communications",
          "CWF Managers",
          "CWF Support Center Employees",
          "CBS",
          "None",
          "Other (Please Specify)",
        ];
        setDistroListsAvailable(addHomeOfficeUserDistroList);
      }
    } else if (props.mode === "update") {
      if (props.employee && props.businessUnitName && props.businessUnitName === "Kansas City") {
        let notFranchiseKcDistroList = [
          "CWFS JS Sold",
          "Accounting Group",
          "IT Communications",
          `CWM-${props.businessUnitName} CBS Sales`,
          "CWM-KS DOOs & FSMs",
          "CWM-KC Night Managers",
          `${props.businessUnitName} - Ops`,
          `${props.businessUnitName} - Sales`,
          "CWFS-KC-AR",
          "CWM-KS Employees",
          `${props.businessUnitName} - Acctg`,
          `${props.businessUnitName} - DL`,
          "CWM-KC FSMs",
          "CWM-KC Sales",
          "None",
          "Other (Please Specify) ",
        ];
        setDistroListsAvailable(notFranchiseKcDistroList);
      } else if (props.employee && props.businessUnitName && props.businessUnitName !== "Kansas City") {
        let notFranchiseNotKcDistroList = [
          "CWFS JS Sold",
          "Accounting Group",
          "IT Communications",
          `${props.businessUnitName} - Ops`,
          `${props.businessUnitName} - Sales`,
          `${props.businessUnitName} - Acctg`,
          `${props.businessUnitName} - Employee DL`,
          "None",
          "Other (Please Specify) ",
        ];
        setDistroListsAvailable(notFranchiseNotKcDistroList);
      } else if (props.typeOfModal === "Franchise") {
        let franchiseDistroList = ["CWFS JS Sold", "Accounting Group", "IT Communications"];
        if (props.employee && props.businessUnitName) {
          franchiseDistroList = franchiseDistroList.concat([
            `${props.businessUnitName} - Ops`,
            `${props.businessUnitName} - Sales`,
            `${props.businessUnitName} - Acctg`,
            `${props.businessUnitName} - Employee DL`,
          ]);
        }
        franchiseDistroList = franchiseDistroList.concat(["None", "Other (Please Specify) "]);
        setDistroListsAvailable(franchiseDistroList);
      }
    }
  }, [userToAdd.companyName, requestType, props.employee, props.typeOfModal, props.businessUnitName, props.mode]);

  const getUser = useCallback(() => {
    if (props.mode === "add") {
      return { ...userToAdd };
    } else if (props.mode === "update") {
      return { ...userToUpdate };
    } else if (props.mode === "remove") {
      return { ...userToRemove };
    }
  }, [props.mode, userToAdd, userToUpdate, userToRemove]);

  const getUpdateUser = useMemo(() => {
    if (props.mode === "add") {
      return setUserToAdd;
    } else if (props.mode === "update") {
      return setUserToUpdate;
    } else if (props.mode === "remove") {
      return setUserToRemove;
    }
  }, [props.mode]);

  const onCalendarClick = (date) => {
    if (date) {
      const formattedDate = DateTime.fromISO(date, { zone: "America/Chicago" }).startOf("day");
      const now = new Date();
      const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
      if (formattedDate <= yesterday) {
        alert("Please select a date in the future.");
      } else {
        setDateTimeOnCalendarInput((prevDateTime) => ({
          ...prevDateTime,
          clickedDate: date,
        }));
        setShowTimeSelectModal(true);
        setClickedDateTime(formattedDate);
      }
    }
  };

  const addTimeToCalendarDate = (timeString) => {
    const [time, amOrPm] = timeString.split(" ");
    let hour = parseInt(time.split(":")[0], 10);
    if (amOrPm === "PM" && hour !== 12) {
      hour += 12;
    }
    if (amOrPm === "AM" && hour === 12) {
      hour = 0;
    }

    const calendarDateTime = clickedDateTime;
    const newDateTime = calendarDateTime.set({ hour: hour, minute: 0, second: 0, millisecond: 0 });
    const newFormattedDateTime = newDateTime.toFormat("yyyy-MM-dd HH:mm:ss.SSS");
    return newFormattedDateTime;
  };

  const onTimeSelect = (time) => {
    if (props.mode === "add") {
      setUserToAdd((prevUserToAdd) => ({
        ...prevUserToAdd,
        effectiveDate: addTimeToCalendarDate(time),
      }));
    } else if (props.mode === "update") {
      setUserToUpdate((prevUserToUpdate) => ({
        ...prevUserToUpdate,
        effectiveDate: addTimeToCalendarDate(time),
      }));
    } else if (props.mode === "remove") {
      setUserToRemove((prevUserToRemove) => ({
        ...prevUserToRemove,
        effectiveDate: addTimeToCalendarDate(time),
      }));
    }
    setDateTimeOnCalendarInput((prevDateTime) => ({
      ...prevDateTime,
      dateToShow: prevDateTime.clickedDate,
    }));
    setShowTimeSelectModal(false);
  };

  const updateUserJobTitle = useCallback(
    (jobTitle) => {
      let updatedUser = {};
      updatedUser.jobTitle = jobTitle;
      if (jobTitle === "Accountant") {
        updatedUser.powerBi = true;
        updatedUser.solver = true;
        updatedUser.businessCentralSecurityRole = "Accounting Clerk";
        updatedUser.fieldSalesAccess = "Power User";
        updatedUser.avalara = true;
        updatedUser.crmClientsAndContracts = true;
        updatedUser.crmContractAndFinancialDetailsAccess = "Full Edit Access";
      }
      if (jobTitle === "Administrative Assistant") {
        updatedUser.powerBi = true;
        updatedUser.solver = false;
        updatedUser.businessCentralSecurityRole = "";
        updatedUser.fieldSalesAccess = "Power User";
        updatedUser.avalara = false;
        updatedUser.crmClientsAndContracts = true;
        updatedUser.crmContractAndFinancialDetailsAccess = "Full Edit Access";
      }
      if (jobTitle === "Business Development Specialist") {
        updatedUser.powerBi = false;
        updatedUser.solver = false;
        updatedUser.businessCentralSecurityRole = "";
        updatedUser.fieldSalesAccess = "N/A";
        updatedUser.avalara = false;
        updatedUser.crmClientsAndContracts = false;
        updatedUser.crmContractAndFinancialDetailsAccess = "None";
      }
      if (jobTitle === "Director of Sales") {
        updatedUser.powerBi = true;
        updatedUser.solver = false;
        updatedUser.businessCentralSecurityRole = "";
        updatedUser.fieldSalesAccess = "N/A";
        updatedUser.avalara = false;
        updatedUser.crmClientsAndContracts = false;
        updatedUser.crmContractAndFinancialDetailsAccess = "Full Edit Access";
      }
      if (jobTitle === "Sales Manager") {
        updatedUser.powerBi = true;
        updatedUser.solver = false;
        updatedUser.businessCentralSecurityRole = "";
        updatedUser.fieldSalesAccess = "N/A";
        updatedUser.avalara = false;
        updatedUser.crmClientsAndContracts = false;
        updatedUser.crmContractAndFinancialDetailsAccess = "Full Edit Access";
      }
      if (jobTitle === "Facility Solutions Manager") {
        updatedUser.powerBi = true;
        updatedUser.solver = false;
        updatedUser.businessCentralSecurityRole = "";
        updatedUser.fieldSalesAccess = "FSM";
        updatedUser.avalara = false;
        updatedUser.crmClientsAndContracts = false;
        updatedUser.crmContractAndFinancialDetailsAccess = "Full Edit Access";
      }
      if (jobTitle === "General Manager") {
        updatedUser.powerBi = true;
        updatedUser.solver = true;
        updatedUser.businessCentralSecurityRole = "";
        updatedUser.fieldSalesAccess = "Power User";
        updatedUser.avalara = false;
        updatedUser.crmClientsAndContracts = false;
        updatedUser.crmContractAndFinancialDetailsAccess = "Full Edit Access";
      }
      if (jobTitle === "Franchisee") {
        updatedUser.powerBi = true;
        updatedUser.solver = true;
        updatedUser.businessCentralSecurityRole = "";
        updatedUser.fieldSalesAccess = "Power User";
        updatedUser.avalara = false;
        updatedUser.crmClientsAndContracts = true;
        updatedUser.crmContractAndFinancialDetailsAccess = "Full Edit Access";
      }
      if (jobTitle === "Sr. Sales Executive") {
        updatedUser.powerBi = true;
        updatedUser.solver = false;
        updatedUser.businessCentralSecurityRole = "";
        updatedUser.fieldSalesAccess = "N/A";
        updatedUser.avalara = false;
        updatedUser.crmClientsAndContracts = false;
        updatedUser.crmContractAndFinancialDetailsAccess = "Full Edit Access";
      }
      if (jobTitle === "Sales Executive") {
        updatedUser.powerBi = true;
        updatedUser.solver = false;
        updatedUser.businessCentralSecurityRole = "";
        updatedUser.fieldSalesAccess = "N/A";
        updatedUser.avalara = false;
        updatedUser.crmClientsAndContracts = false;
        updatedUser.crmContractAndFinancialDetailsAccess = "Full Edit Access";
      }
      if (jobTitle === "Jr. Sales Executive") {
        updatedUser.powerBi = true;
        updatedUser.solver = false;
        updatedUser.businessCentralSecurityRole = "";
        updatedUser.fieldSalesAccess = "N/A";
        updatedUser.avalara = false;
        updatedUser.crmClientsAndContracts = false;
        updatedUser.crmContractAndFinancialDetailsAccess = "Full Edit Access";
      }
      if (jobTitle === "Sales Associate") {
        updatedUser.powerBi = true;
        updatedUser.solver = false;
        updatedUser.businessCentralSecurityRole = "";
        updatedUser.fieldSalesAccess = "N/A";
        updatedUser.avalara = false;
        updatedUser.crmClientsAndContracts = false;
        updatedUser.crmContractAndFinancialDetailsAccess = "Full Edit Access";
      }
      if (jobTitle === "Operations Manager") {
        updatedUser.powerBi = true;
        updatedUser.solver = false;
        updatedUser.businessCentralSecurityRole = "";
        updatedUser.fieldSalesAccess = "Power User";
        updatedUser.avalara = false;
        updatedUser.crmClientsAndContracts = false;
        updatedUser.crmContractAndFinancialDetailsAccess = "Full Edit Access";
      }
      if (jobTitle === "Director of Operations") {
        updatedUser.powerBi = true;
        updatedUser.solver = true;
        updatedUser.businessCentralSecurityRole = "";
        updatedUser.fieldSalesAccess = "Power User";
        updatedUser.avalara = false;
        updatedUser.crmClientsAndContracts = false;
        updatedUser.crmContractAndFinancialDetailsAccess = "Full Edit Access";
      }
      if (jobTitle === "Night Manager") {
        updatedUser.powerBi = false;
        updatedUser.solver = false;
        updatedUser.businessCentralSecurityRole = "";
        updatedUser.fieldSalesAccess = "N/A";
        updatedUser.avalara = false;
        updatedUser.crmClientsAndContracts = false;
        updatedUser.crmContractAndFinancialDetailsAccess = "None";
      }
      if (jobTitle === "CBS Sales Executive") {
        updatedUser.powerBi = true;
        updatedUser.solver = false;
        updatedUser.businessCentralSecurityRole = "";
        updatedUser.fieldSalesAccess = "Power User";
        updatedUser.avalara = false;
        updatedUser.crmClientsAndContracts = false;
        updatedUser.crmContractAndFinancialDetailsAccess = "Full Edit Access";
      }
      if (jobTitle === "Human Resources") {
        updatedUser.powerBi = false;
        updatedUser.solver = false;
        updatedUser.businessCentralSecurityRole = "";
        updatedUser.fieldSalesAccess = "N/A";
        updatedUser.avalara = false;
        updatedUser.crmClientsAndContracts = false;
        updatedUser.crmContractAndFinancialDetailsAccess = "Read Only";
      }
      if (jobTitle === "Office Manager") {
        updatedUser.powerBi = false;
        updatedUser.solver = false;
        updatedUser.businessCentralSecurityRole = "";
        updatedUser.fieldSalesAccess = "N/A";
        updatedUser.avalara = false;
        updatedUser.crmClientsAndContracts = false;
        updatedUser.crmContractAndFinancialDetailsAccess = "Full Edit Access";
      }
      if (jobTitle === "IC Recruiter") {
        updatedUser.powerBi = false;
        updatedUser.solver = false;
        updatedUser.businessCentralSecurityRole = "";
        updatedUser.fieldSalesAccess = "N/A";
        updatedUser.avalara = false;
        updatedUser.crmClientsAndContracts = false;
        updatedUser.crmContractAndFinancialDetailsAccess = "Read Only";
      }
      if (props.mode === "add") {
        setUserToAdd((prev) => ({ ...prev, ...updatedUser }));
      } else if (props.mode === "update") {
        setUserToUpdate((prev) => ({ ...prev, ...updatedUser }));
      }
    },
    [props.mode]
  );

  const updateDistroList = (value) => {
    const updatedUser = getUser();
    let newDistroLists = new Set(updatedUser.accessList.split(", ").filter((distroList) => distroList !== ""));
    if (newDistroLists.has(value)) {
      newDistroLists.delete(value);
    } else {
      newDistroLists.add(value);
    }
    if (props.mode === "add") {
      setUserToAdd((prevUserToAdd) => ({
        ...prevUserToAdd,
        accessList: [...newDistroLists].join(", "),
      }));
    } else if (props.mode === "update") {
      setUserToUpdate((prevUserToUpdate) => ({
        ...prevUserToUpdate,
        accessList: [...newDistroLists].join(", "),
      }));
    }
  };

  const updateLocationsSupporting = (value) => {
    if (props.mode == "add") {
      let prevLocationsSupporting = userToAdd.locationsSupporting;
      let newLocationsSupporting = "";

      if (prevLocationsSupporting === "") {
        newLocationsSupporting = value;
      } else if (prevLocationsSupporting.includes(value)) {
        newLocationsSupporting = prevLocationsSupporting.replace(value, "");

        // These are here to make sure that the extra commas are trimmed if the user selects and deselects a location
        if (newLocationsSupporting.charAt(0) === ",") {
          newLocationsSupporting = newLocationsSupporting.slice(1);
        }
        if (newLocationsSupporting.includes(", ,")) {
          newLocationsSupporting = newLocationsSupporting.replace(", ,", ",");
        }
        if (newLocationsSupporting.charAt(newLocationsSupporting.length - 2) === ",") {
          newLocationsSupporting = newLocationsSupporting.slice(0, -2);
        }
      } else {
        newLocationsSupporting = prevLocationsSupporting + ", " + value;

        // There are here because of a weird corner case where True and False are valid values, which I'm not entirely sure why that is allowed
        if (newLocationsSupporting.includes("True") && value === "False") {
          newLocationsSupporting = newLocationsSupporting.replace("True", "");
        } else if (newLocationsSupporting.includes("False") && value === "True") {
          newLocationsSupporting = newLocationsSupporting.replace("False", "");
        }
      }

      setUserToAdd((prevUserToAdd) => ({
        ...prevUserToAdd,
        locationsSupporting: newLocationsSupporting,
      }));
    } else if (props.mode === "update") {
      let updatedUser = userToUpdate;
      let prevLocationsSupporting = updatedUser.locationsSupporting;
      let newLocationsSupporting = "";
      if (prevLocationsSupporting === "") {
        newLocationsSupporting = value;
      } else {
        newLocationsSupporting = prevLocationsSupporting + ", " + value;
      }
      setUserToUpdate((prevUserToUpdate) => ({
        ...prevUserToUpdate,
        locationsSupporting: newLocationsSupporting,
      }));
    }
  };

  const updateBusinessUnit = (value) => {
    setUserToAdd((prevUserToAdd) => ({
      ...prevUserToAdd,
      companyName: value.target.value,
      showFranchiseAddUser: false,
      showKcAddUser: false,
    }));
    determineWhichAddUserToDisplayAndDisplay(value.target.value);
  };

  const combineOtherDistrosListWithDistrosList = () => {
    const updatedUser = getUser();
    if (updatedUser.accessList !== "") {
      updatedUser.accessList += ", " + updatedUser.otherDistros;
    } else if (updatedUser.OtherDistros !== "") {
      updatedUser.accessList = updatedUser.otherDistros;
    }
    setUserToAdd(updatedUser);
  };

  const validateForm = () => {
    const updatedUser = getUser();

    if (updatedUser.specialInstruction) {
      if (!updatedUser.specialInstruction.match(/^[a-zA-Z0-9@',.\n ?]+$/)) {
        // TODO: Why do we exclude these lol
        alert(
          "You can only use letters, @, ', and , within the Special Instructions input box, please correct this and try again."
        );
        return false;
      }
    }
    if (!updatedUser.effectiveDate || updatedUser.effectiveDate == "") {
      alert("You must select a valid date");
      return false;
    }

    if (props.mode === "add" || props.mode === "update") {
      if (!updatedUser.jobTitle) {
        alert("Please Select a Job Title");
        return false;
      }
      if (updatedUser.replacement && !updatedUser.personReplacing) {
        alert("Please enter who the user is replacing");
        return false;
      }
      if (updatedUser.personReplacing) {
        if (!updatedUser.personReplacing.match(/^[a-zA-Z0-9@ .]+$/)) {
          alert(
            "You can only use letters, numbers, @, and periods within the Person Replacing Other input box, please correct this and try again."
          );
          return false;
        }
      }
      if (props.mode === "add") {
        if (!updatedUser.companyName) {
          alert("You must select a company");
          return false;
        }
      } else if (props.mode === "update") {
        if (updatedUser.notify) {
          if (!updatedUser.notify.match(/^[a-zA-Z0-9@ .]+$/)) {
            alert(
              "You can only use letters, numbers, @, and periods within the notify Other input box, please correct this and try again."
            );
            return false;
          }
        }
      }
      return true;
    } else if (props.mode === "remove") {
      if (updatedUser.howDidEmployeeLeave === "") {
        alert("Please Select if the Employee Resigned or was Terminated.");
        return false;
      }
      if (updatedUser.whyDidEmployeeLeave === "") {
        alert("You must select a reason for why the employee is leaving City Wide.");
        return false;
      }
      if (updatedUser.whyDidEmployeeLeave === "Other") {
        alert("Please Provide More Info For Why The Employee Left.");
        return false;
      }
      if (!updatedUser.emailDuration || updatedUser.emailDuration === "") {
        alert("You must select a length to keep the users email.");
        return false;
      }
      if (
        (!updatedUser.emailRecipient || updatedUser.emailRecipient === "") &&
        updatedUser.emailDuration !== "Do Not Keep Email"
      ) {
        alert("You must select a user to forward the removed users emails to.");
        return false;
      }
      if (updatedUser.notifyOther) {
        if (!updatedUser.notifyOther.match(/^[a-zA-Z@. ]+$/)) {
          alert("You can only use letters and @ within the Notify Other input box, please correct this and try again.");
          return false;
        }
      }
      return true;
    }
  };

  const onSubmitClick = async () => {
    setSubmitLoading(true);
    const username = sessionStorage.getItem("username");

    if (props.mode === "add") {
      if (validateForm()) {
        let addUserForm = {};
        addUserForm.RequestId = uuidv4();
        addUserForm.Company = userToAdd.companyName;
        addUserForm.CreatedDate = new Date();
        addUserForm.RequestedBy = username;
        addUserForm.EffectiveDate = new Date(userToAdd.effectiveDate).toISOString().slice(0, 19);
        addUserForm.UserFullName = userToAdd.fullName || "";
        addUserForm.PhoneNumber1 = userToAdd.phoneNumber1 || "";
        addUserForm.PhoneNumber2 = userToAdd.phoneNumber2 || "";

        if (userToAdd.jobTitle && userToAdd.jobTitle === "Other (Please Specify)")
          addUserForm.JobTitle = userToAdd.otherJobTitle;
        else addUserForm.JobTitle = userToAdd.jobTitle ? userToAdd.jobTitle : "";

        if (userToAdd.accessList) {
          combineOtherDistrosListWithDistrosList();
          addUserForm.OtherDistros = userToAdd.accessList;
        } else addUserForm.OtherDistros = "";

        addUserForm.SpecialInstructions = userToAdd.specialInstruction || "";
        addUserForm.Notify = userToAdd.notify || "";
        addUserForm.BusinessCentralSecurityRole = userToAdd.businessCentralSecurityRole || "";
        addUserForm.FieldSalesAccess = userToAdd.fieldSalesAccess || "";
        addUserForm.Avalara = userToAdd.avalara;
        addUserForm.ApproveClientsAndContracts = userToAdd.crmClientsAndContracts;
        addUserForm.ContractAndFinancialDetailsAccess = userToAdd.crmContractAndFinancialDetailsAccess || "";
        addUserForm.PowerBi = userToAdd.powerBi;
        addUserForm.JetHub = userToAdd.jetHub || false;
        addUserForm.Solver = userToAdd.solver;
        addUserForm.CityWideSocial = userToAdd.cityWideSocial || false;
        addUserForm.CityWideYouAdmin = userToAdd.cityWideYouAdmin || false;
        addUserForm.AcrobatPro = userToAdd.acrobatPro || false;
        addUserForm.HubStaff = userToAdd.hubStaff || false;
        addUserForm.ZendeskAgent = userToAdd.zendeskAgent || false;
        addUserForm.AdobeCreativeCloud = userToAdd.adobeCreativeCloud || false;
        addUserForm.MaxAgent = userToAdd.maxAgent || false;
        addUserForm.TrainingEnvironments = userToAdd.trainingEnvironments || false;
        addUserForm.Monitors = userToAdd.needsMonitors || false;
        addUserForm.DeskPhone = userToAdd.deskPhone || false;
        addUserForm.Headset = userToAdd.needsHeadset || false;
        addUserForm.OfficeLocation = userToAdd.officeSpace || "";
        addUserForm.Replacement = userToAdd.replacement || false;
        addUserForm.ReplacingFullName = userToAdd.personReplacing || "";
        addUserForm.PhoneNumber1Type = userToAdd.phoneNumber1Type || "";
        addUserForm.PhoneNumber1Ext = userToAdd.phoneNumber1Ext || "";
        addUserForm.PhoneNumber2Type = userToAdd.phoneNumber2Type || "";
        addUserForm.PhoneNumber2Ext = userToAdd.phoneNumber2Ext || "";
        addUserForm.IsRemote = userToAdd.isRemote || false;
        addUserForm.NeedsOffice = userToAdd.needsOffice || false;
        addUserForm.LocationsSupporting = userToAdd.locationsSupporting;
        addUserForm.DirectReport = userToAdd.directReport || "";
        addUserForm.localMarketingChampions = userToAdd.localMarketingChampions || "";

        try {
          await insertAddUserRequest(addUserForm);
          addNotification({ show: true, type: "Success", description: "User Add Request Submitted Successfully" });
        } catch (error) {
          console.error(error);
          addNotification({
            show: true,
            type: "Error",
            description: (
              <>
                Add User Request Failed. We ran into an issue submitting your request. Please contact
                <b>itsupport@gocitywide.com</b> for assistance.
              </>
            ),
          });
        }
        resetUserValuesAndClose("add");
      }
    } else if (props.mode === "update") {
      if (validateForm()) {
        let updateUserForm = {};
        updateUserForm.RequestId = uuidv4();
        updateUserForm.Company = props.businessUnitName;
        updateUserForm.CreatedDate = new Date();
        updateUserForm.RequestedBy = username;
        updateUserForm.effectiveDate = userToUpdate.effectiveDate
          ? new Date(userToUpdate.effectiveDate).toISOString().slice(0, 19)
          : new Date().toISOString().slice(0, 19);
        updateUserForm.UserFullName = userToUpdate.UpdatedUserFullName
          ? userToUpdate.UpdatedUserFullName
          : props.employee.userFullName;
        updateUserForm.UpdatedUserFullName = userToUpdate.UpdatedUserFullName
          ? userToUpdate.UpdatedUserFullName
          : props.employee.userFullName;
        updateUserForm.OtherDistros = userToUpdate.accessList || "";
        updateUserForm.phoneNumber1 = userToUpdate.phoneNumber1 || "";
        updateUserForm.phoneNumber2 = userToUpdate.phoneNumber2 || "";
        updateUserForm.specialInstructions = userToUpdate.specialInstruction || "";
        updateUserForm.notify = userToUpdate.notify || "";
        updateUserForm.businessCentralSecurityRole = userToUpdate.businessCentralSecurityRole || "";
        updateUserForm.fieldSalesAccess = userToUpdate.fieldSalesAccess || "";
        updateUserForm.avalara = userToUpdate.avalara;
        updateUserForm.ApproveClientsAndContracts = userToUpdate.crmClientsAndContracts;
        updateUserForm.ContractAndFinancialDetailsAccess = userToUpdate.crmContractAndFinancialDetailsAccess || "";
        updateUserForm.powerBi = userToUpdate.powerBi;
        updateUserForm.jetHub = userToUpdate.jetHub || false;
        updateUserForm.Solver = userToUpdate.solver;
        updateUserForm.SoCi = userToUpdate.soCi || false;
        updateUserForm.CityWideYouAdmin = userToUpdate.cityWideYouAdmin || false;
        updateUserForm.AcrobatPro = userToUpdate.acrobatPro || false;
        updateUserForm.HubStaff = userToUpdate.hubStaff || false;
        updateUserForm.ZendeskAgent = userToUpdate.zendeskAgent || false;
        updateUserForm.AdobeCreativeCloud = userToUpdate.adobeCreativeCloud || false;
        updateUserForm.MaxAgent = userToUpdate.maxAgent ? userToUpdate.maxAgent : false;
        updateUserForm.TrainingEnvironments = userToUpdate.trainingEnvironments || false;
        updateUserForm.Monitors = userToUpdate.monitors || false;
        updateUserForm.DeskPhone = userToUpdate.deskPhone || false;
        updateUserForm.Headset = userToUpdate.headset || false;
        updateUserForm.OfficeSpace = userToUpdate.officeSpace || false;
        updateUserForm.replacement = userToUpdate.replacement || false;
        updateUserForm.personReplacing = userToUpdate.personReplacing || "";
        updateUserForm.phoneNumber1Type = userToUpdate.phoneNumber1Type || "";
        updateUserForm.phoneNumber1Ext = userToUpdate.phoneNumber1Ext || "";
        updateUserForm.phoneNumber2Type = userToUpdate.phoneNumber2Type || "";
        updateUserForm.phoneNumber2Ext = userToUpdate.phoneNumber2Ext || "";
        updateUserForm.locationsSupporting = userToUpdate.locationsSupporting;
        updateUserForm.OfficeLocation = userToUpdate.officeLocation || "";
        updateUserForm.localMarketingChampions = userToUpdate.localMarketingChampions || "";

        if (userToUpdate.jobTitle && userToUpdate.jobTitle === "Other (Please Specify)") {
          updateUserForm.jobTitle = userToUpdate.otherJobTitle;
        } else if (userToUpdate.jobTitle) {
          updateUserForm.jobTitle = userToUpdate.jobTitle;
        } else {
          updateUserForm.jobTitle = "";
        }

        if (userToUpdate.accessList) {
          combineOtherDistrosListWithDistrosList();
          updateUserForm.OtherDistros = userToUpdate.accessList;
        } else {
          updateUserForm.OtherDistros = "";
        }

        try {
          await insertUpdateUserRequest(updateUserForm);
          addNotification({ show: true, type: "Success", description: "User Update Request Submitted Successfully" });
        } catch (error) {
          console.error(error);
          addNotification({
            show: true,
            type: "Error",
            description: (
              <>
                Update User Request Failed. We ran into an issue submitting your request. Please contact
                <b> itsupport@gocitywide.com</b> for assistance.
              </>
            ),
          });
        }
        resetUserValuesAndClose("update");
      }
    } else if (props.mode === "remove") {
      if (validateForm()) {
        let removeUserForm = {};
        removeUserForm.RequestId = uuidv4();
        removeUserForm.Company = props.employee.userBusinessUnit;
        removeUserForm.CreatedDate = Moment(new Date()).subtract(5, "hours");
        removeUserForm.RequestedBy = username;
        removeUserForm.UserFullName = props.employee.userFullName;
        removeUserForm.UserPosition = props.employee.userJobTitle;
        removeUserForm.UserBundle = props.employee.userBundleName;
        removeUserForm.EmailAccess = true;
        removeUserForm.EmailAccessDuration = userToRemove.emailDuration;
        removeUserForm.emailRecipient = userToRemove.emailRecipient;
        removeUserForm.RequestedTimeFrame = new Date(userToRemove.effectiveDate).toISOString().slice(0, 19);
        removeUserForm.UserDomainName = props.employee.userDomainName;
        removeUserForm.SpecialInstruction = userToRemove.specialInstruction || "";
        removeUserForm.NotifyFranchisee = userToRemove.notifyFranchisee;
        removeUserForm.NotifyOther = userToRemove.notifyOther || "";
        removeUserForm.IsInTwoAccountDisabled = false;
        removeUserForm.IsCrmAccountDisabled = false;
        removeUserForm.IsEmailAccountModified = false;
        removeUserForm.IsUnplanned = userToRemove.isUnplanned;
        removeUserForm.cityWideYou = userToRemove.cityWideYou;
        removeUserForm.whyDidEmployeeLeave = userToRemove.whyDidEmployeeLeave;
        removeUserForm.howDidEmployeeLeave = userToRemove.howDidEmployeeLeave;
        try {
          await insertRemoveUserRequest(removeUserForm);
          addNotification({ show: true, type: "Success", description: "User Remove Request Submitted Successfully" });
        } catch (error) {
          addNotification({
            show: true,
            type: "Error",
            description: (
              <>
                Remove User Request Failed. We ran into an issue submitting your request. Please contact
                <b>itsupport@gocitywide.com</b> for assistance.
              </>
            ),
          });
        }
        resetUserValuesAndClose("remove");
      }
    }
    setSubmitLoading(false);
  };

  const determineWhichAddUserToDisplayAndDisplay = (companyName) => {
    if (
      companyName === "Kansas City" ||
      companyName === "Minnesota" ||
      companyName === "Silicon Valley" ||
      companyName === "Northern Virginia" ||
      companyName === "KC - Window Washing"
    ) {
      setRequestType("AddKCUser");
    } else if (companyName === "Support Center") {
      setRequestType("AddHomeOfficeUser");
    } else {
      setRequestType("AddFranchiseUser");
    }
  };

  const resetUserValuesAndClose = (typeOfModal) => {
    if (typeOfModal === "add") {
      setRequestType("");
      setUserToAdd({
        fullName: "",
        domainName: "",
        companyName: "",
        licenseBundleId: "",
        showFranchiseAddUser: false,
        showKcAddUser: false,
        accessList: "",
        locationsSupporting: "",
        jobTitle: "",
        powerBi: false,
        solver: false,
        avalara: false,
        crmClientsAndContracts: false,
      });
    } else if (typeOfModal === "update") {
      setUserToUpdate({
        effectiveDate: "",
        showTimeSelectModal: false,
        selectedDateTime: null,
        showDistroSelectModal: false,
        isUnplanned: false,
        notifyFranchisee: false,
        submitLoading: false,
        accessList: "",
        phoneNumber1: "",
        phoneNumber1Type: "",
        phoneNumber1Ext: "",
        phoneNumber2: "",
        phoneNumber2Type: "",
        phoneNumber2Ext: "",
        jobTitle: "",
        powerBi: false,
        jetHub: false,
        businessCentralSecurityRole: "",
        fieldSalesAccess: "",
        avalara: false,
        crmClientsAndContracts: false,
        crmContractAndFinancialDetailsAccess: "",
        specialInstructions: "",
        replacement: false,
        personReplacing: "",
        notify: "",
        otherJobTitle: "",
        locationsSupporting: "",
        updatedUserFullName: "",
        solver: false,
      });
    } else if (typeOfModal === "remove") {
      setUserToRemove({
        licenseBundleId: "",
        effectiveDate: "",
        EmailDuration: "",
        emailRecipient: "",
        howDidEmployeeLeave: "",
        whyDidEmployeeLeave: "",
        showTimeSelectModal: false,
        selectedDateTime: null,
        isUnplanned: false,
        notifyFranchisee: false,
        submitLoading: false,
        cityWideYou: false,
        powerBi: false,
        solver: false,
        avalara: false,
        crmClientsAndContracts: false,
      });
    }
    props.onHide();
  };

  const toggleRemovedUserField = (nameOfField) => {
    if (typeof userToRemove[nameOfField] === "boolean") {
      setUserToRemove((prevUserToRemove) => ({
        ...prevUserToRemove,
        [nameOfField]: !userToRemove[nameOfField],
      }));
    }
  };

  const userCurrentJobTitle = useMemo(() => {
    return props.mode === "update" && props.employee ? props.employee.userJobTitle : "";
  }, [props.employee, props.mode]);

  return (
    <div>
      {(props.mode === "add" || props.mode === "update") && (
        <Modal
          show={props.show}
          onHide={() => resetUserValuesAndClose(props.mode === "add" ? "add" : "update")}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title id="contained-modal-title-vcenter">
              {props.mode === "add" && <>Add User</>}
              {props.mode === "update" && props.employee && (
                <>
                  <h5>
                    Update User <i>{props.employee.userFullName}</i> - {props.employee.userDomainName}
                  </h5>
                  <h5>
                    {props.employee.userJobTitle} - {props.employee.userBundleName} Bundle
                  </h5>
                  <h5>{props.businessUnitName}</h5>
                </>
              )}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-2" style={{ display: "flex", justifyContent: "space-between", gap: "10px" }}>
              <UserNameSection
                userToUpdate={getUser()}
                setUser={getUpdateUser}
                mode={props.mode}
                employeeName={props.mode === "update" && props.employee ? props.employee.userFullName : ""}
              />
              {props.mode === "add" && (
                <CompanySection businessUnitsList={props.businessUnitsList} updateBusinessUnit={updateBusinessUnit} />
              )}
            </div>
            <div className="my-3" style={{ display: "flex", justifyContent: "space-between" }}>
              <PhoneNumberSection
                user={getUser()}
                setUser={getUpdateUser}
                addSecondNumber={addSecondNumber}
                setAddSecondNumber={setAddSecondNumber}
              />
              <DateSection
                onCalendarClick={onCalendarClick}
                user={getUser()}
                dateTimeOnCalendarInput={dateTimeOnCalendarInput}
              />
            </div>
            <JobTitleSection
              mode={props.mode}
              user={getUser()}
              setUser={getUpdateUser}
              jobs={jobs}
              updateUserJobTitle={updateUserJobTitle}
              bundlesList={props.bundlesList}
              requestType={requestType}
              typeOfModal={props.typeOfModal}
              updateLocationsSupporting={updateLocationsSupporting}
              userCurrentJobTitle={userCurrentJobTitle}
              userLicenseBundle={props.employee ? props.employee.userBundleName : ""}
            />
            {props.mode === "add" && (
              <>
                {requestType == "AddHomeOfficeUser" && (
                  <>
                    <div className="my-3">
                      <div style={{ display: "flex", gap: "10px" }}>
                        Should this user support all locations?
                        <Checkbox
                          value="supportAllYesRadio"
                          onClick={() =>
                            updateLocationsSupporting(
                              !userToAdd.locationsSupporting.includes("True") ? "True" : "False"
                            )
                          }
                          checked={userToAdd.locationsSupporting.includes("True")}
                        />
                      </div>
                      <div style={{ display: "flex", gap: "10px" }}>
                        Will this user be remote?
                        <Checkbox
                          value="remoteYesRadio"
                          onClick={() =>
                            setUserToAdd((prevUserToAdd) => ({
                              ...prevUserToAdd,
                              isRemote: !userToAdd.isRemote,
                            }))
                          }
                          checked={userToAdd.isRemote}
                        />
                      </div>
                    </div>
                    <DistroListsSection
                      distroListsAvailable={distroListsAvailable}
                      updateDistroList={updateDistroList}
                      user={userToAdd}
                      setUser={setUserToAdd}
                    />
                  </>
                )}
                {requestType == "AddKCUser" && (
                  <>
                    {userToAdd.companyName && userToAdd.companyName === "Kansas City" && (
                      <DistroListsSection
                        distroListsAvailable={distroListsAvailable}
                        updateDistroList={updateDistroList}
                        user={userToAdd}
                        setUser={setUserToAdd}
                      />
                    )}
                    {userToAdd.companyName && userToAdd.companyName !== "Kansas City" && (
                      <DistroListsSection
                        distroListsAvailable={distroListsAvailable}
                        updateDistroList={updateDistroList}
                        user={userToAdd}
                        setUser={setUserToAdd}
                      />
                    )}
                  </>
                )}
                {requestType == "AddFranchiseUser" && (
                  <DistroListsSection
                    distroListsAvailable={distroListsAvailable}
                    updateDistroList={updateDistroList}
                    user={userToAdd}
                    setUser={setUserToAdd}
                  />
                )}
                <UserAddOnSection mode="add" requestType={requestType} user={userToAdd} setUser={setUserToAdd} />
                <ExtraInfoSection user={userToAdd} setUser={setUserToAdd} mode="add" requestType={requestType} />
              </>
            )}
            {props.mode === "update" && (
              <>
                {props.typeOfModal === "NotFranchise" && (
                  <>
                    {props.employee &&
                      props.employee.userBusinessUnit &&
                      props.employee.userBusinessUnit === "Kansas City" && (
                        <DistroListsSection
                          distroListsAvailable={distroListsAvailable}
                          updateDistroList={updateDistroList}
                          user={userToUpdate}
                          setUser={setUserToUpdate}
                        />
                      )}
                    {props.employee &&
                      props.employee.userBusinessUnit &&
                      props.employee.userBusinessUnit !== "Kansas City" && (
                        <DistroListsSection
                          distroListsAvailable={distroListsAvailable}
                          updateDistroList={updateDistroList}
                          user={userToUpdate}
                          setUser={setUserToUpdate}
                        />
                      )}
                    <UserAddOnSection
                      mode="update"
                      requestType={requestType}
                      user={userToUpdate}
                      setUser={setUserToUpdate}
                    />
                  </>
                )}
                {props.typeOfModal === "Franchise" && (
                  <DistroListsSection
                    distroListsAvailable={distroListsAvailable}
                    updateDistroList={updateDistroList}
                    user={userToUpdate}
                    setUser={setUserToUpdate}
                  />
                )}
                <div className="my-3" style={{ display: "flex", gap: "10px" }}>
                  Is this user a replacement?
                  <Checkbox
                    onClick={() =>
                      setUserToUpdate((prevUserToUpdate) => ({
                        ...prevUserToUpdate,
                        replacement: !prevUserToUpdate.replacement,
                      }))
                    }
                    checked={userToUpdate.replacement}
                  />
                </div>
                {userToUpdate.replacement && (
                  <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                    <span style={{ width: "255px" }}>Who is being replaced?</span>
                    <Form.Control
                      type="text"
                      onChange={(value) =>
                        setUserToUpdate((prevUserToUpdate) => ({
                          ...prevUserToUpdate,
                          personReplacing: value.target.value,
                        }))
                      }
                    />
                  </div>
                )}
                <ExtraInfoSection
                  user={userToUpdate}
                  setUser={setUserToUpdate}
                  mode="update"
                  typeOfModal={props.typeOfModal}
                />
              </>
            )}
          </Modal.Body>
          <ModalFooter
            mode={props.mode}
            submitLoading={submitLoading}
            onSubmitClick={onSubmitClick}
            onHide={props.onHide}
            resetUserValuesAndClose={() => resetUserValuesAndClose(props.mode)}
          />
        </Modal>
      )}
      {props.mode === "remove" && (
        <Modal show={props.show} onHide={() => resetUserValuesAndClose("remove")} size="lg" centered>
          <Modal.Header closeButton>
            {props.employee && (
              <Modal.Title id="contained-modal-title-vcenter">
                <h5>
                  Remove <i>{props.employee.userBundleName}</i> License Bundle from <i>{props.employee.userFullName}</i>
                </h5>
                <h5>{props.employee.userDomainName}</h5>
                <h5>{props.employee.userJobTitle}</h5>
              </Modal.Title>
            )}
          </Modal.Header>
          <Modal.Body>
            <div className="mb-2">
              <DateSection
                onCalendarClick={onCalendarClick}
                user={userToRemove}
                dateTimeOnCalendarInput={dateTimeOnCalendarInput}
                mode="remove"
              />
            </div>
            <div className="my-3" style={{ display: "flex", gap: "10px" }}>
              <Form.Group style={{ width: "50%" }}>
                How long should the email be kept?
                <Form.Control
                  as="select"
                  custom
                  onChange={(value) =>
                    setUserToRemove((prevUserToRemove) => ({
                      ...prevUserToRemove,
                      emailDuration: value.target.value,
                    }))
                  }
                >
                  <option>Click Here For Options</option>
                  <option>15 Days</option>
                  <option>30 Days</option>
                  <option>Until Remove Email Request Is Submitted</option>
                  <option>Do Not Keep Email</option>
                </Form.Control>
              </Form.Group>
              {userToRemove.emailDuration && userToRemove.emailDuration !== "Do Not Keep Email" && (
                <Form.Group style={{ width: "50%" }}>
                  <EmailSearchBlock
                    usersList={props.usersList}
                    userToRemove={userToRemove}
                    setUserToRemove={setUserToRemove}
                  />
                </Form.Group>
              )}
            </div>
            <div className="my-3">
              <b>You will be charged for the email bundle until the time frame selected above has been completed.</b>
            </div>
            <div className="my-3" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div style={{ display: "flex", gap: "10px" }}>
                <span style={{ whiteSpace: "nowrap" }}>Notify Franchisee?</span>
                <Checkbox
                  onClick={() => toggleRemovedUserField("notifyFranchisee")}
                  checked={userToRemove.NotifyFranchisee}
                />
              </div>
              <div style={{ display: "flex", gap: "10px", alignItems: "center", width: "100%" }}>
                <span style={{ whiteSpace: "nowrap" }}>Notify Other?</span>
                <Form.Control
                  type="text"
                  custom
                  onChange={(value) =>
                    setUserToRemove((prevUserToRemove) => ({
                      ...prevUserToRemove,
                      notifyOther: value.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <div className="my-3" style={{ display: "flex", gap: "10px" }}>
              <div style={{ display: "flex", gap: "10px" }}>
                Remove User from City Wide YOU?
                <Checkbox onClick={() => toggleRemovedUserField("cityWideYou")} checked={userToRemove.cityWideYou} />
              </div>
              <div style={{ display: "flex", gap: "10px" }}>
                Unplanned Termination/Resignation?
                <Checkbox
                  onClick={() => toggleRemovedUserField("isUnplanned")}
                  checked={userToRemove.NotifyFranchisee}
                />
              </div>
            </div>
            <div className="my-3" style={{ display: "flex", gap: "10px" }}>
              Employee Resigned
              <RadioButton
                value="Employee Resigned"
                checked={userToRemove.howDidEmployeeLeave === "Employee Resigned"}
                onChange={(e) =>
                  setUserToRemove((prevUserToRemove) => ({
                    ...prevUserToRemove,
                    howDidEmployeeLeave: e.target.value,
                  }))
                }
              />
              Employee was Terminated
              <RadioButton
                value="Employee was Terminated"
                checked={userToRemove.howDidEmployeeLeave === "Employee was Terminated"}
                onChange={(e) =>
                  setUserToRemove((prevUserToRemove) => ({
                    ...prevUserToRemove,
                    howDidEmployeeLeave: e.target.value,
                  }))
                }
              />
            </div>
            <Form.Group className="my-3">
              What is the main factor that contributed to the employee leaving City Wide?
              <Form.Control
                as="select"
                onChange={(e) =>
                  setUserToRemove((prevUserToRemove) => ({
                    ...prevUserToRemove,
                    whyDidEmployeeLeave: e.target.value,
                  }))
                }
              >
                <option value="">SELECT ONE</option>
                <option value="Career Advancement Opportunity">Career Advancement Opportunity</option>
                <option value="Work Ethic">Work Ethic</option>
                <option value="Bad Fit">Bad Fit</option>
                <option value="Compensation">Compensation</option>
                <option value="Performance">Performance</option>
                <option value="Management">Management</option>
                <option value="Training">Training</option>
                <option value="Never Started">Never Started</option>
                <option value="Other">Other</option>
              </Form.Control>
              {userToRemove.whyDidEmployeeLeave.slice(0, 5) === "Other" && (
                <div className="my-2">
                  <Form.Control
                    as="textarea"
                    custom
                    onChange={(e) =>
                      setUserToRemove((prevUserToRemove) => ({
                        ...prevUserToRemove,
                        whyDidEmployeeLeave: "Other: " + e.target.value,
                      }))
                    }
                  />
                </div>
              )}
            </Form.Group>
            <Form.Group className="my-3">
              Special Instructions:
              <Form.Control
                as="textarea"
                custom
                onChange={(value) =>
                  setUserToRemove((prevUserToRemove) => ({
                    ...prevUserToRemove,
                    specialInstruction: value.target.value,
                  }))
                }
              />
            </Form.Group>
            <div className="mt-3">
              <strong>
                NOTE:
                <ul>
                  <li>If the user is in multiple locations, you must submit a remove user form for each account.</li>
                  <li>Please download training records from PowerBI immediately upon termination.</li>
                </ul>
              </strong>
            </div>
          </Modal.Body>
          <ModalFooter
            mode="remove"
            submitLoading={submitLoading}
            onSubmitClick={onSubmitClick}
            onHide={() => resetUserValuesAndClose("remove")}
          />
        </Modal>
      )}
      <TimeOfDayModal
        show={showTimeSelectModal}
        onHide={() => setShowTimeSelectModal(false)}
        onTimeSelect={(value) => onTimeSelect(value)}
      />
    </div>
  );
};

export default UserFormModal;
